import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import StorefrontLayout from './StorefrontLayout';
import { useAccount } from '../accountContext';

const emptyAddress = {
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal: '',
    country: 'US',
};

const formatMoney = (amount) => {
    if (amount == null || Number.isNaN(Number(amount))) {
        return '';
    }
    return `$${Number(amount).toFixed(2)} USD`;
};

const formatDate = (value) => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const Account = () => {
    const {
        user,
        orders,
        support,
        checking,
        signup,
        login,
        logout,
        saveProfile,
        openStripePortal,
    } = useAccount();
    const [mode, setMode] = useState('signin');
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [busy, setBusy] = useState(false);
    const [portalError, setPortalError] = useState('');
    const [auth, setAuth] = useState({ name: '', email: '', password: '' });
    const [profile, setProfile] = useState({
        name: '',
        phone: '',
        address: emptyAddress,
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = 'Account · Delaney Stewart';
    }, []);

    useEffect(() => {
        if (!user) {
            return;
        }
        setProfile({
            name: user.name || '',
            phone: user.phone || '',
            address: { ...emptyAddress, ...(user.address || {}) },
        });
    }, [user]);

    const onAuth = async (event) => {
        event.preventDefault();
        setError('');
        setBusy(true);
        try {
            if (mode === 'signup') {
                await signup(auth);
            } else {
                await login(auth);
            }
            setAuth({ name: '', email: '', password: '' });
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const onSave = async (event) => {
        event.preventDefault();
        setError('');
        setSaved(false);
        setBusy(true);
        try {
            await saveProfile(profile);
            setSaved(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const onPortal = async () => {
        setPortalError('');
        setBusy(true);
        try {
            await openStripePortal();
        } catch (err) {
            setPortalError(err.message);
            setBusy(false);
        }
    };

    if (checking) {
        return (
            <StorefrontLayout>
                <main className="accountPage">
                    <p className="accountCopy">Loading your account…</p>
                </main>
            </StorefrontLayout>
        );
    }

    if (!user) {
        return (
            <StorefrontLayout>
                <main className="accountPage">
                    <p className="accountKicker">Account</p>
                    <h1 className="accountTitle">{mode === 'signup' ? 'Create an account' : 'Sign in'}</h1>
                    <p className="accountLead">
                        Save a shipping address, see past orders, and check out faster.
                    </p>
                    <form className="accountCard accountForm" onSubmit={onAuth}>
                        {mode === 'signup' ?
                            <label>
                                Name
                                <input
                                    value={auth.name}
                                    onChange={(event) => setAuth((prev) => ({ ...prev, name: event.target.value }))}
                                    autoComplete="name"
                                />
                            </label>
                            : null}
                        <label>
                            Email
                            <input
                                type="email"
                                required
                                value={auth.email}
                                onChange={(event) => setAuth((prev) => ({ ...prev, email: event.target.value }))}
                                autoComplete="email"
                            />
                        </label>
                        <label>
                            Password
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={auth.password}
                                onChange={(event) => setAuth((prev) => ({ ...prev, password: event.target.value }))}
                                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                            />
                        </label>
                        {error ? <p className="accountError">{error}</p> : null}
                        <button className="accountButton" type="submit" disabled={busy}>
                            {busy ? 'Please wait…' : (mode === 'signup' ? 'Create account' : 'Sign in')}
                        </button>
                    </form>
                    <p className="accountSwitch">
                        {mode === 'signup' ?
                            <>Already have an account? <button type="button" onClick={() => { setMode('signin'); setError(''); }}>Sign in</button></>
                            :
                            <>New here? <button type="button" onClick={() => { setMode('signup'); setError(''); }}>Create an account</button></>
                        }
                    </p>
                </main>
            </StorefrontLayout>
        );
    }

    return (
        <StorefrontLayout>
            <main className="accountPage">
                <p className="accountKicker">Account</p>
                <h1 className="accountTitle">{user.name ? `Hello, ${user.name.split(' ')[0]}` : 'Your studio profile'}</h1>
                <p className="accountLead">{user.email}</p>

                <form className="accountCard" onSubmit={onSave}>
                    <h2>Shipping address</h2>
                    <p className="accountCopy">Used to prefill checkout when you order prints or stickers.</p>
                    <div className="accountGrid">
                        <label>
                            Name
                            <input
                                value={profile.name}
                                onChange={(event) => setProfile((prev) => ({ ...prev, name: event.target.value }))}
                                autoComplete="name"
                            />
                        </label>
                        <label>
                            Phone
                            <input
                                value={profile.phone}
                                onChange={(event) => setProfile((prev) => ({ ...prev, phone: event.target.value }))}
                                autoComplete="tel"
                            />
                        </label>
                        <label className="accountWide">
                            Address
                            <input
                                value={profile.address.line1}
                                onChange={(event) => setProfile((prev) => ({
                                    ...prev,
                                    address: { ...prev.address, line1: event.target.value },
                                }))}
                                autoComplete="address-line1"
                            />
                        </label>
                        <label className="accountWide">
                            Apartment, suite
                            <input
                                value={profile.address.line2}
                                onChange={(event) => setProfile((prev) => ({
                                    ...prev,
                                    address: { ...prev.address, line2: event.target.value },
                                }))}
                                autoComplete="address-line2"
                            />
                        </label>
                        <label>
                            City
                            <input
                                value={profile.address.city}
                                onChange={(event) => setProfile((prev) => ({
                                    ...prev,
                                    address: { ...prev.address, city: event.target.value },
                                }))}
                                autoComplete="address-level2"
                            />
                        </label>
                        <label>
                            State
                            <input
                                value={profile.address.state}
                                onChange={(event) => setProfile((prev) => ({
                                    ...prev,
                                    address: { ...prev.address, state: event.target.value },
                                }))}
                                autoComplete="address-level1"
                            />
                        </label>
                        <label>
                            ZIP
                            <input
                                value={profile.address.postal}
                                onChange={(event) => setProfile((prev) => ({
                                    ...prev,
                                    address: { ...prev.address, postal: event.target.value },
                                }))}
                                autoComplete="postal-code"
                            />
                        </label>
                    </div>
                    {error ? <p className="accountError">{error}</p> : null}
                    {saved ? <p className="accountSaved">Address saved.</p> : null}
                    <button className="accountButton" type="submit" disabled={busy}>
                        {busy ? 'Saving…' : 'Save address'}
                    </button>
                </form>

                <section className="accountCard">
                    <h2>Past orders</h2>
                    {orders.length === 0 ?
                        <p className="accountCopy">
                            Orders placed with this email will appear here after checkout.
                            {' '}
                            <Link to="/shop">Browse the shop</Link>
                        </p>
                        :
                        <ul className="accountOrders">
                            {orders.map((order) => (
                                <li key={order.id}>
                                    <div>
                                        <strong>{order.receiptNumber || 'Studio order'}</strong>
                                        <span>{formatDate(order.fulfilledAt)}</span>
                                    </div>
                                    <p>
                                        {(order.items || []).map((item) => `${item.name} × ${item.quantity}`).join(', ') || 'Print order'}
                                    </p>
                                    <p>{formatMoney(order.amount)}</p>
                                    {order.receiptUrl ?
                                        <a href={order.receiptUrl} target="_blank" rel="noreferrer">View receipt</a>
                                        : null}
                                </li>
                            ))}
                        </ul>
                    }
                    {user.stripeCustomerId ?
                        <>
                            <button className="accountGhost" type="button" onClick={onPortal} disabled={busy}>
                                Manage cards with Stripe
                            </button>
                            {portalError ? <p className="accountError">{portalError}</p> : null}
                        </>
                        : null}
                </section>

                <section className="accountCard">
                    <h2>Contact us</h2>
                    <p className="accountCopy">Studio support for orders, shipping, and damaged prints.</p>
                    <dl className="accountContact">
                        {support.phone ?
                            <div>
                                <dt>Phone</dt>
                                <dd><a href={`tel:${support.phone.replace(/\s+/g, '')}`}>{support.phone}</a></dd>
                            </div>
                            : null}
                        {support.email ?
                            <div>
                                <dt>Email</dt>
                                <dd><a href={`mailto:${support.email}`}>{support.email}</a></dd>
                            </div>
                            : null}
                        <div>
                            <dt>Instagram</dt>
                            <dd>
                                <a href={support.instagram} target="_blank" rel="noreferrer">
                                    {support.instagramHandle}
                                </a>
                            </dd>
                        </div>
                        <div>
                            <dt>Studio</dt>
                            <dd>Leucadia, California</dd>
                        </div>
                    </dl>
                    {!support.phone && !support.email ?
                        <p className="accountCopy">Message @dnystu on Instagram for order questions.</p>
                        : null}
                </section>

                <button className="accountSignOut" type="button" onClick={logout}>Sign out</button>
            </main>
        </StorefrontLayout>
    );
};

export default Account;
